// Gera payload EMV Pix (BR Code) estático com valor definido.
// Referência: Manual BR Code / Banco Central.

function crc16(payload) {
  let crc = 0xFFFF;
  for (let i = 0; i < payload.length; i++) {
    crc ^= payload.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1);
      crc &= 0xFFFF;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

// EMV length in BYTES, not UTF-16 units — banks reject the QR otherwise.
function byteLen(str) {
  return new TextEncoder().encode(str).length;
}

function field(id, value) {
  const len = byteLen(value).toString().padStart(2, '0');
  return `${id}${len}${value}`;
}

function sanitize(str, max) {
  const clean = (str || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9 ]/g, '')
    .toUpperCase();
  // Trim by BYTES, not chars, to stay within EMV field limits.
  let out = clean;
  while (byteLen(out) > max) out = out.slice(0, -1);
  return out;
}

/**
 * Normalize a Pix key to the exact format the BCB expects for field 01.
 * Bad formatting is the #1 reason banks reject the QR ("Chave Pix inválida").
 *
 *  - email    → trim + lowercase
 *  - cpf      → 11 digits, no punctuation
 *  - cnpj     → 14 digits, no punctuation
 *  - phone    → E.164 with leading '+' and country code (default +55 for Brazil)
 *  - random   → 36-char UUID, lowercase, keep hyphens
 *
 * Auto-detects the type when `type` is omitted / unknown, based on the key
 * shape (photographer profiles don't store the type — only the raw key).
 */
export function normalizePixKey(key, type = '') {
  const raw = String(key || '').trim();
  if (!raw) return '';

  let t = (type || '').toLowerCase();

  // Auto-detect when caller doesn't pass a type.
  if (!t || !['email', 'cpf', 'cnpj', 'phone', 'telefone', 'random', 'aleatoria', 'aleatória'].includes(t)) {
    if (raw.includes('@')) t = 'email';
    else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) t = 'random';
    else {
      const digits = raw.replace(/\D/g, '');
      if (digits.length === 11 && !raw.startsWith('+')) t = 'cpf';
      else if (digits.length === 14) t = 'cnpj';
      else t = 'phone';
    }
  }

  if (t === 'email') return raw.toLowerCase();
  if (t === 'cpf') return raw.replace(/\D/g, '').slice(0, 11);
  if (t === 'cnpj') return raw.replace(/\D/g, '').slice(0, 14);
  if (t === 'random' || t === 'aleatoria' || t === 'aleatória') return raw.toLowerCase();

  // phone / telefone → E.164
  let digits = raw.replace(/\D/g, '');
  // Strip a leading Brazilian trunk "0" that people sometimes type
  if (digits.length === 12 && digits.startsWith('0')) digits = digits.slice(1);
  // Prepend country code if missing (11 = mobile with 9, 10 = landline)
  if (digits.length === 10 || digits.length === 11) digits = '55' + digits;
  return '+' + digits;
}

export function buildPixPayload({ key, amount, merchantName = 'AXSELL', city = 'SAO PAULO', txid = 'AXSELL', keyType = '' }) {
  const normalizedKey = normalizePixKey(key, keyType);
  if (!normalizedKey) return '';

  const gui = field('00', 'BR.GOV.BCB.PIX');
  const keyField = field('01', normalizedKey);
  const merchantAccount = field('26', gui + keyField);

  const amt = Number(amount || 0).toFixed(2);
  const cleanName = sanitize(merchantName, 25) || 'AXSELL';
  const cleanCity = sanitize(city, 15) || 'SAO PAULO';
  const cleanTxid = sanitize(txid, 25) || '***';

  const payload =
    field('00', '01') +
    merchantAccount +
    field('52', '0000') +
    field('53', '986') +
    (Number(amt) > 0 ? field('54', amt) : '') +
    field('58', 'BR') +
    field('59', cleanName) +
    field('60', cleanCity) +
    field('62', field('05', cleanTxid)) +
    '6304';

  const crc = crc16(payload);
  return payload + crc;
}
